import { isServer, keepPreviousData, QueryClient } from '@tanstack/react-query';
import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { routeTree } from './routeTree.gen';
import {
	persistQueryClient,
	PersistQueryClientProvider,
	removeOldestQuery,
} from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { PERSISTED_QUERY_CACHE_KEY } from './lib/query-persistence';
import type { ReactNode } from 'react';
import { gtag, sanitizeAnalyticsUrl } from '@worldcoo/ui/lib/analytics';
import { getRuntimeAnalyticsCode } from './lib/shared';
import { createLocaleI18n, loadLandingTranslation } from './lib/i18n';
import { errorReportData, extractErrorInfo, getRequestContext, logErrorFn } from './lib/data';
import { scriptErrorLocation } from './lib/script-error-location';
import { baseUrlFromHost, portalIdFromHost } from './lib/request-host';
import {
	isApplePayReporterMounted,
	isApplePaySessionAbortError,
	isClientFaultRpcError,
	isHydrationError,
	shouldReportFrontendError,
} from './lib/frontend-error-filter';
import { describeHydrationRewrite } from './lib/hydration-rewrite-monitor';
import { installSessionActionTracking, recentBreadcrumbs } from './lib/session-actions';
import { isUnauthorizedError } from './lib/ngo-session';
import { portalLocales, type PortalLocale } from '@worldcoo/monolith/portals';
import { I18nProvider } from '@lingui/react';
import { DefaultCatchBoundary, isVersionSkewReloadInProgress } from './routes/-components/default-catch-boundary';
import { NotFound } from './routes/-components/not-found';
// From `consent-cookie`, not `usercentrics`: this module is bundled and executed on the server too, and
// that one is browser-only. The call below is guarded by `!isServer`, but an import is not.
import { readConsentCookie } from '@worldcoo/ui/lib/consent-cookie';
import { portalSlugs, wwwSlugs } from './lib/slug-rewrites';
import { detectLocale } from '@worldcoo/monolith/core';

/**
 * Client-only handle on the active router's locale state, so language switching can retarget the
 * output rewrite (see {@link updateRouterLocale}). There is exactly one router per browser session,
 * which makes a module-level handle correct here.
 *
 * It must NEVER be read during render. It used to back an exported `hasLocalePrefix()`, and because
 * every `getRouter()` call overwrote it, render-time readers in the long-lived SSR worker got
 * whichever request had most recently built a router — a cross-request race that made the
 * server-rendered canonical URL disagree with the client's and threw React #418 hydration
 * mismatches on unprefixed URLs (TEC-3607). Render code reads `hasLocalePrefix` off the
 * per-request router context instead (see `MyRouterContext` in `routes/__root.tsx`).
 */
let _clientLocaleState: { locale: string; hasLocalePrefix: boolean } | undefined;

/** Update the current locale used by the rewrite output (called from language switching). */
export function updateRouterLocale(locale: string) {
	if (_clientLocaleState) {
		_clientLocaleState.locale = locale;
		_clientLocaleState.hasLocalePrefix = true;
	}
}

/**
 * The browser session's query client, published for the error reporter alone.
 *
 * Client-only and module-scoped for the same reason as `_clientLocaleState` above: there is exactly
 * one router per browser session, whereas on the server one long-lived worker builds a router per
 * request, so a module-level handle there would hand one request's cache to another. Left undefined on
 * the server, and every reader must tolerate that.
 *
 * Why the reporter wants it: the dehydrated cache holds the data the server rendered this document
 * from, which makes it the app's own record of what its text SHOULD say — the witnesses
 * `wasOurTextReplaced` matches recorded rewrites against. Read through {@link clientQueryClient} at
 * the point of use; never captured at module scope by callers.
 */
let _clientQueryClient: QueryClient | undefined;

export function clientQueryClient(): QueryClient | undefined {
	return _clientQueryClient;
}

function isPortalLocale(segment: string | undefined): segment is string {
	return !!segment && (portalLocales as readonly string[]).includes(segment);
}

/**
 * TODO: We're using the tanstack start built-in RPC instead of trpc because trpc
 * needs to send the proper headers to the backend and this disables tanstack query persist
 * We lose the batching from TRPC but we gain the ability to persist queries
 */
export async function getRouter() {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				staleTime: 60 * 1000,
				gcTime: Infinity,
				placeholderData: keepPreviousData,
				// On the SERVER, never retry: SSR reads suspend, so retrying just holds the Suspense
				// boundary open across attempts (each attempt + backoff) for no benefit. Fail fast during
				// SSR to the cache's stale/fallback value (or an error boundary); the client still retries
				// after hydration, where a retry is cheap and genuinely useful for a transient error.
				// A 401 is never retried either: it's a decided answer, so surface it immediately to the
				// catch boundary (login redirect) instead of stalling behind three backoff retries.
				retry: (failureCount, error) => !isServer && !isUnauthorizedError(error) && failureCount < 3,
			},
		},
	});

	const persister = createAsyncStoragePersister({
		key: PERSISTED_QUERY_CACHE_KEY,
		storage: typeof window !== 'undefined' ? window.localStorage : undefined,
		retry: removeOldestQuery,
	});

	const buster = 'v4';

	// Cookies are the source of truth for auth. Persisting auth queries would make the
	// previous user's identity survive a page reload until the background refetch lands.
	const dehydrateOptions = {
		shouldDehydrateQuery: (query: { queryKey: readonly unknown[]; state: { status: string; data?: unknown } }) => {
			const first = query.queryKey[0];
			if (Array.isArray(first) && first[0] === 'auth') return false;
			if (query.state.status !== 'success') return false;
			return true;
		},
	};

	// TODO: We're calling it also here instead of relying only on <PersistQueryClientProvider />
	// because on quick reload is still not restoring from persister
	if (!isServer) {
		persistQueryClient({ queryClient, persister, buster, dehydrateOptions });
	}

	// Server: compute request context from headers once. Client: placeholder until hydrate().
	const serverContext = isServer ? await getRequestContext() : undefined;

	/**
	 * The locale the SERVER rendered this document with, read back on the client from the `<html lang>`
	 * attribute it wrote.
	 *
	 * `hydrate()` below installs the real dehydrated context, but that payload is injected late in the SSR
	 * stream, so a first client render can happen before it lands. The placeholder it fell back to was a
	 * hard-coded `'es'`, which meant that render disagreed with the server on every ENGLISH page:
	 * `formatNumber` produced `1.234,56` against the server's `1,234.56`, React reported the text mismatch
	 * as #418, and the donation counters briefly showed the wrong separators (TEC-3689).
	 *
	 * That is also why only `/en/` ever reported it — `normalizeLocale` collapses `es`/`ca`/`gl`/`eu`/`it`
	 * onto the same separators, so a wrong `'es'` placeholder is invisible for all of them. English is the
	 * only locale whose formatting can disagree, which is exactly the shape of the production reports.
	 *
	 * `<html lang>` is written by the server render itself, so seeding from it agrees by construction
	 * rather than by winning a race. `detectLocale` validates it, so an unexpected value still falls back.
	 * Same source `loadLandingTranslation` below already trusts for the very same reason.
	 */
	const documentLocale =
		!isServer && typeof document !== 'undefined' && document.documentElement.lang
			? detectLocale(document.documentElement.lang, portalLocales)
			: undefined;

	/**
	 * Same race, remaining fields. `hydrate()` also delivers `portalId` and `baseUrl`, and a first
	 * client render that loses the race used to see the empty-string placeholders — a suspense query
	 * keyed on `portalId: ''` misses the dehydrated cache, suspends, and the boundary client-renders
	 * against the server's markup (React #418, `args[]=HTML`).
	 *
	 * The contract for every placeholder here is "equals the value `hydrate()` will install, by
	 * construction". Field by field:
	 *
	 * - `portalId` / `baseUrl`: pure functions of the host. Computed by the SAME helpers
	 *   `parseRequest` uses (`request-host.ts`), fed `location.host` — the full authority including
	 *   any port, matching the `Host` header the server derived from. Never hand-mirror these.
	 * - `locale`: `documentLocale` above (from `<html lang>`, written by the server render itself).
	 *   `detectLocale` always returns a valid locale (it falls back to 'es' internally), so the
	 *   final `?? 'es'` only covers a document with no `lang` at all (an error shell).
	 * - `isInIframe`: the server ORs two signals — the `?iframe=true` search param and the
	 *   `Sec-Fetch-*` headers. The param half is mirrored exactly from the location; the header
	 *   half is not knowable from the client (`window.top` origin checks answer cross-ORIGIN, not
	 *   the header's cross-SITE, so mirroring it would guess), and stays false until `hydrate()`.
	 * - `isBot`: constant `false` placeholder. Its only reader, `ensureSeoData`, is gated on
	 *   `isServer`, so the client value is never consumed before `hydrate()` replaces it. If a
	 *   client-side reader is ever added, it must tolerate (or re-derive) this placeholder.
	 */
	const documentHost = !isServer ? window.location.host : undefined;

	const requestContext = {
		baseUrl: serverContext?.baseUrl ?? (documentHost ? baseUrlFromHost(documentHost) : ''),
		portalId: serverContext?.portalId ?? (documentHost ? portalIdFromHost(documentHost) : ''),
		locale: serverContext?.locale ?? documentLocale ?? 'es',
		isBot: serverContext?.isBot ?? false,
		isInIframe:
			serverContext?.isInIframe ?? (!isServer && new URLSearchParams(window.location.search).get('iframe') === 'true'),
		// Mirrored from the same cookie on both sides, so the first client render agrees with the markup it is
		// hydrating. Reading it from the cookie rather than from the dehydrated context on the client is what
		// keeps them identical: it is the one source both passes can actually see. See `syncConsentCookie`.
		consentDecision: serverContext?.consentDecision ?? (!isServer ? readConsentCookie() : undefined),
	};

	// Mutable locale state captured by rewrite closures — scoped per router instance
	const localeState: { locale: string; hasLocalePrefix: boolean } = {
		locale: requestContext.locale,
		hasLocalePrefix: true,
	};
	if (!isServer) _clientLocaleState = localeState;
	if (!isServer) _clientQueryClient = queryClient;

	// Request-scoped Lingui instance. On the server this isolates each request's active
	// locale from concurrent renders sharing the process (prevents React #418 hydration
	// mismatches); on the client it is the shared global. See createLocaleI18n.
	const i18nInstance = createLocaleI18n();

	// Shared mutable context object — the rewrite.input closure updates `locale` and
	// `hasLocalePrefix` before the first render so SSR and client agree on both.
	const routerContext = { queryClient, i18n: i18nInstance, hasLocalePrefix: true, ...requestContext };

	await loadLandingTranslation(
		isServer ? requestContext.locale : (document.documentElement.lang as PortalLocale) || 'es',
		i18nInstance,
	);

	const router = createTanStackRouter({
		routeTree,
		context: routerContext,
		// Dehydrate server-computed request context (including URL-detected locale)
		// so the client receives it without a server function call on every navigation.
		dehydrate: () => ({
			requestContext: {
				...requestContext,
				locale: detectLocale(localeState.locale, portalLocales),
				// Carried across so the client's first render resolves the same canonical URL shape the
				// markup was rendered with, instead of re-deriving it and risking a #418 (TEC-3607).
				hasLocalePrefix: localeState.hasLocalePrefix,
			},
		}),
		hydrate: (dehydrated) => {
			if (dehydrated.requestContext) {
				localeState.locale = dehydrated.requestContext.locale;
				localeState.hasLocalePrefix = dehydrated.requestContext.hasLocalePrefix;
				if (router.options.context) {
					Object.assign(router.options.context, dehydrated.requestContext);
				}
			}
		},
		defaultPreload: 'intent',
		scrollRestoration: true,
		rewrite: {
			input: ({ url }) => {
				// Skip rewriting for routes that must live at the root level
				if (url.pathname.startsWith('/media/')) return url;
				if (url.pathname.startsWith('/api/trpc')) return url;
				// Host-agnostic donation-confirmed notification from the monolith — see routes/api.donations.$donationId.ts
				if (url.pathname.startsWith('/api/donations/')) return url;

				const hostParts = url.hostname.split('.');
				const isMainDomain =
					hostParts[0] === 'www' ||
					(hostParts.length < 3 && hostParts.at(-1) !== 'localhost') ||
					(hostParts.length < 2 && hostParts.at(-1) === 'localhost');

				const prefix = isMainDomain ? 'www' : hostParts[0]!;
				url.pathname = `/${prefix}${url.pathname}`;

				if (prefix !== 'ngos') {
					const segments = url.pathname.split('/').filter(Boolean);
					if (isPortalLocale(segments[1])) {
						localeState.locale = segments[1];
						localeState.hasLocalePrefix = true;
						// Keep the router context in sync so SSR renders the correct
						// locale (e.g. <html lang="en">) and canonical URL on the very first pass.
						const resolvedLocale = detectLocale(segments[1], portalLocales);
						routerContext.locale = resolvedLocale;
						routerContext.hasLocalePrefix = true;
						segments.splice(1, 1);
						url.pathname = '/' + segments.join('/');
					} else {
						localeState.hasLocalePrefix = false;
						routerContext.hasLocalePrefix = false;
					}
				}

				// 3. Rewrite legal page slugs to internal route names
				if (prefix !== 'ngos') {
					const segments = url.pathname.split('/').filter(Boolean);
					const maps = prefix === 'www' ? wwwSlugs : portalSlugs;
					const routeName = segments[1] ? maps.slugToRoute[segments[1]] : undefined;
					if (segments.length === 2 && routeName) {
						segments[1] = routeName;
						url.pathname = '/' + segments.join('/');
					}
				}

				return url;
			},
			output: ({ url }) => {
				// Skip rewriting for routes that must live at the root level
				if (url.pathname.startsWith('/media/')) return url;
				if (url.pathname.startsWith('/api/trpc')) return url;
				// Host-agnostic donation-confirmed notification from the monolith — see routes/api.donations.$donationId.ts
				if (url.pathname.startsWith('/api/donations/')) return url;

				const parts = url.pathname.split('/').filter(Boolean);
				if (parts.length === 0) return url;

				const prefix = parts[0];

				// Don't add locale prefix for ngos routes
				if (prefix === 'ngos') {
					url.pathname = '/' + parts.slice(1).join('/') || '/';
					return url;
				}

				// Rewrite internal legal route names back to locale-specific slugs
				if (prefix !== 'ngos' && parts.length === 2 && parts[1]) {
					const maps = prefix === 'www' ? wwwSlugs : portalSlugs;
					if (maps.routeToSlugs[parts[1]]) {
						parts[1] = maps.routeToSlugs[parts[1]]![localeState.locale] ?? parts[1];
					}
				}

				// Strip subdomain prefix; add locale prefix only if the incoming URL had one
				const rest = parts.slice(1).join('/');
				url.pathname = localeState.hasLocalePrefix ? `/${localeState.locale}${rest ? '/' + rest : ''}` : `/${rest}`;
				return url;
			},
		},
		// Since we're using React Query, we don't want loader calls to ever be stale
		// This will ensure that the loader is always called when the route is preloaded or visited
		defaultPreloadStaleTime: 0,
		// Blank default: every route OWNS its pending UI via its own skeleton (co-located with the route —
		// see the `<Suspense>` fallbacks in the dashboard/projects/portals routes). This default just
		// ensures routes WITHOUT one still get wrapped in a Suspense boundary that renders nothing, so
		// their data/chunk suspense is caught in place (the real server-rendered chrome stays put) instead
		// of bubbling up and blanking the whole app.
		defaultPendingComponent: () => null,
		Wrap: (props: { children: ReactNode }) => {
			return (
				<PersistQueryClientProvider client={queryClient} persistOptions={{ persister, buster, dehydrateOptions }}>
					{props.children}
				</PersistQueryClientProvider>
			);
		},
		defaultErrorComponent: (props) => (
			<I18nProvider i18n={i18nInstance}>
				<DefaultCatchBoundary {...props} />
			</I18nProvider>
		),
		defaultNotFoundComponent: () => <NotFound />,
	});

	setupRouterSsrQueryIntegration({
		router,
		queryClient,
		wrapQueryClient: false,
	});

	if (!isServer) {
		/**
		 * Setup google analytics
		 */
		gtag('js', new Date());
		// `config` auto-sends the initial page_view from the current location, so the
		// landing URL must be sanitized here too, not only on later navigations.
		gtag('config', getRuntimeAnalyticsCode(), {
			page_location: sanitizeAnalyticsUrl(window.location.href),
		});

		router.history.subscribe((url) => {
			gtag('event', 'page_view', { page_location: sanitizeAnalyticsUrl(url.location.href) });
		});

		// Record the session timeline (navigations + clicks by accessible name) as `client_action` wide
		// events, and keep the last few as breadcrumbs on any crash reported below. See session-actions.ts
		// for the strict limits on what may be collected.
		installSessionActionTracking((onNavigate) => {
			router.history.subscribe((url) => {
				onNavigate(url.location.pathname);
			});
		});

		/**
		 * Reload the page if it fails to load dynamic imports
		 * When a new deployment occurs, the hosting service may delete the assets from previous deployments.
		 * As a result, a user who visited your site before the new deployment might encounter an import error.
		 * This error happens because the assets running on that user's device are outdated and it tries to import
		 * the corresponding old chunk, which is deleted.
		 * This event is useful for addressing this situation. In this case, make sure to set Cache-Control: no-cache on the HTML file,
		 * otherwise the old assets will be still referenced.
		 * See: https://vite.dev/guide/build#load-error-handling
		 */
		window.addEventListener('vite:preloadError', () => {
			window.location.reload();
		});

		/**
		 * Global error handlers for errors that escape React error boundaries
		 * (async callbacks, event handlers, setTimeout, etc.)
		 */
		window.addEventListener('error', (event) => {
			if (isVersionSkewReloadInProgress()) return;
			// Cross-origin script errors (reCAPTCHA, GA, etc.) report event.error as null.
			// These are not actionable — skip logging them.
			if (event.error == null) return;
			// A FORBIDDEN the server refused on purpose is the caller's fault, not a system error — checked
			// on the error OBJECT, which only exists here, before it is reduced to strings.
			if (isClientFaultRpcError(event.error)) return;
			const errorInfo = extractErrorInfo(event.error);
			// A hydration error on a document something else has rewritten is that rewriter's mismatch, not
			// ours. Literally the same gate as the hydrateRoot handlers in client.tsx, by calling the same
			// function: this path used to re-implement it with the translator signal alone, which is blind to
			// the text-only rewrites of TEC-3703 and kept ticketing them (TEC-3696 family).
			const rewrite = isHydrationError(errorInfo.message) ? describeHydrationRewrite(clientQueryClient()) : undefined;
			if (rewrite?.foreign) return;
			// Drop environmental / third-party / transient noise (browser extensions, page translators,
			// reCAPTCHA/GA, stale-chunk aborts) so it doesn't spam PORTAL_FRONTEND_ERROR tickets.
			if (!shouldReportFrontendError(errorInfo.name, errorInfo.message, errorInfo.stack, errorInfo.cause)) return;
			logErrorFn({
				data: errorReportData('[GLOBAL] ', errorInfo, {
					source: 'global',
					// Which script, and where in it. On a parse-time `SyntaxError` this is the ONLY locator:
					// the script never ran, so there is no stack and no frame to attribute it with, and a
					// report like TEC-3747's could name 320 failures without naming a single script.
					script: scriptErrorLocation(event),
					breadcrumbs: recentBreadcrumbs(),
					// Why a hydration report got past the gate, on this path too — otherwise "pristine page" and
					// "check never ran" are the same silence here as they were in client.tsx.
					counters: rewrite?.attribution,
					// See the `mismatch` note in `client.tsx`: React ships no diff for `args[]=text` in production.
					mismatch: rewrite?.mismatch,
				}),
			}).catch((logErr) => {
				console.error('Failed to log error to backend:', logErr);
			});
		});

		window.addEventListener('unhandledrejection', (event) => {
			if (isVersionSkewReloadInProgress()) return;
			// See the `error` handler above: client-fault responses never ticket, judged on the object.
			if (isClientFaultRpcError(event.reason)) return;
			const errorInfo = extractErrorInfo(event.reason);
			// Same rewrite gate as the `error` handler above (TEC-3696 family).
			const rewrite = isHydrationError(errorInfo.message) ? describeHydrationRewrite(clientQueryClient()) : undefined;
			if (rewrite?.foreign) return;
			// While the checkout route is mounted it reports this one itself, naming the Apple Pay path it
			// escaped from and telling the donor what happened (TEC-3637); reporting it here too would
			// ticket the same failure twice, once without any of that context. Gated on the route's
			// listener actually being mounted so the signature is never dropped by both handlers.
			if (
				isApplePayReporterMounted() &&
				isApplePaySessionAbortError(errorInfo.name, errorInfo.message, errorInfo.stack)
			) {
				return;
			}
			if (!shouldReportFrontendError(errorInfo.name, errorInfo.message, errorInfo.stack, errorInfo.cause)) return;
			logErrorFn({
				data: errorReportData('[UNHANDLED REJECTION] ', errorInfo, {
					source: 'unhandledrejection',
					breadcrumbs: recentBreadcrumbs(),
					// See the `error` handler above.
					counters: rewrite?.attribution,
					// See the `mismatch` note in `client.tsx`: React ships no diff for `args[]=text` in production.
					mismatch: rewrite?.mismatch,
				}),
			}).catch((logErr) => {
				console.error('Failed to log error to backend:', logErr);
			});
		});
	}

	return router;
}
